// Telegram-канал (личный аккаунт через MTProto) — сервис API.
// Базовый префикс: /api/v1/integrations/providers/telegram
// Контракт: handoff §3 (14 эндпоинтов). Парсер ошибок: telegramErrorParser.

import authService from './auth.service';
import { buildQuery } from '../utils/buildQuery';
import {
  parseTelegramErrorResponse,
  networkError,
  TelegramApiError,
} from '../utils/telegramErrorParser';
import type {
  TelegramInstanceOut,
  TelegramInstanceCreatePayload,
  TelegramCredentialsUpdatePayload,
  TelegramDeleteInstanceResult,
  TelegramSessionOut,
  TelegramStartSessionPayload,
  TelegramConfirmPayload,
  TelegramDisconnectResult,
  TelegramSendTextPayload,
  TelegramSendMediaPayload,
  TelegramChatOut,
  TelegramMessageOut,
  TelegramChatClosePayload,
  TelegramMarkReadResult,
} from '../types/telegram.types';

class TelegramService {
  private readonly BASE = '/api/v1/integrations/providers/telegram';

  // ─────────────── Инстансы (каналы) ───────────────

  // Список каналов организации (RBAC-filtered по ролям). scope read.
  async listInstances(opts?: { is_active?: boolean }): Promise<TelegramInstanceOut[]> {
    return this.request<TelegramInstanceOut[]>(
      `${this.BASE}/instances${buildQuery({ is_active: opts?.is_active })}`,
    );
  }

  // Шаг 0 онбординга: создать канал. scope manage.
  async createInstance(body: TelegramInstanceCreatePayload): Promise<TelegramInstanceOut> {
    return this.request<TelegramInstanceOut>(`${this.BASE}/instances`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  // Ротация api_id/api_hash. Запрещена при connected-сессии (409 credentials_locked). scope manage.
  async updateCredentials(
    instanceId: number,
    body: TelegramCredentialsUpdatePayload,
  ): Promise<TelegramInstanceOut> {
    return this.request<TelegramInstanceOut>(`${this.BASE}/instances/${instanceId}/credentials`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  // Удалить канал ЦЕЛИКОМ: logout в Telegram → entity_links → чаты → сессии.
  // НЕОБРАТИМО — caller обязан показать строгий confirm. scope manage.
  async deleteInstance(instanceId: number): Promise<TelegramDeleteInstanceResult> {
    return this.request<TelegramDeleteInstanceResult>(`${this.BASE}/instances/${instanceId}`, {
      method: 'DELETE',
    });
  }

  // ─────────────── Сессии (онбординг) ───────────────

  // Шаг 1: запросить код (Telegram отправит его в приложение владельца номера).
  // scope manage.
  async startSession(
    instanceId: number,
    body: TelegramStartSessionPayload,
  ): Promise<TelegramSessionOut> {
    return this.request<TelegramSessionOut>(`${this.BASE}/instances/${instanceId}/sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  // Статус сессии — восстановление визарда после F5 и карточка канала. scope read.
  async getSession(sessionId: number): Promise<TelegramSessionOut> {
    return this.request<TelegramSessionOut>(`${this.BASE}/sessions/${sessionId}`);
  }

  // Шаг 2/3: код ИЛИ пароль 2FA (после status=password_required). scope manage.
  async confirmSession(
    sessionId: number,
    body: TelegramConfirmPayload,
  ): Promise<TelegramSessionOut> {
    return this.request<TelegramSessionOut>(`${this.BASE}/sessions/${sessionId}/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  // Logout аккаунта (чаты и история в CRM остаются). scope manage.
  async disconnectSession(sessionId: number): Promise<TelegramDisconnectResult> {
    return this.request<TelegramDisconnectResult>(`${this.BASE}/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  }

  // ─────────────── Отправка ───────────────

  // chat_id здесь — peer id СТРОКОЙ (TelegramChatOut.chat_id, не .id!). scope send.
  async sendText(sessionId: number, body: TelegramSendTextPayload): Promise<TelegramMessageOut> {
    return this.request<TelegramMessageOut>(`${this.BASE}/sessions/${sessionId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  // Multipart; лимит 20 МБ проверяется на клиенте ДО вызова. scope send.
  async sendMedia(
    sessionId: number,
    payload: TelegramSendMediaPayload,
  ): Promise<TelegramMessageOut> {
    const fd = new FormData();
    fd.append('chat_id', payload.chat_id);
    if (payload.caption) fd.append('caption', payload.caption);
    fd.append('file', payload.file, payload.file.name);
    return this.requestForm<TelegramMessageOut>(
      `${this.BASE}/sessions/${sessionId}/messages/media`,
      fd,
    );
  }

  // ─────────────── Чаты / сообщения (инбокс) ───────────────

  // status_filter: одно значение или CSV ('new,active'). Сортировка last_message_at DESC.
  async listChats(
    instanceId: number,
    opts?: { status_filter?: string; limit?: number; offset?: number },
  ): Promise<TelegramChatOut[]> {
    return this.request<TelegramChatOut[]>(
      `${this.BASE}/instances/${instanceId}/chats${buildQuery({
        status_filter: opts?.status_filter,
        limit: opts?.limit,
        offset: opts?.offset,
      })}`,
    );
  }

  // История по НАШЕМУ int id чата. timestamp DESC (новые первыми).
  async listMessages(
    chatId: number,
    opts?: { limit?: number; offset?: number },
  ): Promise<TelegramMessageOut[]> {
    return this.request<TelegramMessageOut[]>(
      `${this.BASE}/chats/${chatId}/messages${buildQuery({ limit: opts?.limit, offset: opts?.offset })}`,
    );
  }

  // Сбросить unread (оператор открыл чат). scope read.
  async markChatRead(chatId: number): Promise<TelegramMarkReadResult> {
    return this.request<TelegramMarkReadResult>(`${this.BASE}/chats/${chatId}/read`, {
      method: 'POST',
    });
  }

  // Тело опционально целиком. scope manage.
  async closeChat(chatId: number, body?: TelegramChatClosePayload): Promise<TelegramChatOut> {
    return this.request<TelegramChatOut>(`${this.BASE}/chats/${chatId}/close`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body ?? {}),
    });
  }

  // scope manage.
  async reopenChat(chatId: number): Promise<TelegramChatOut> {
    return this.request<TelegramChatOut>(`${this.BASE}/chats/${chatId}/reopen`, {
      method: 'POST',
    });
  }

  // ─────────────── private ───────────────

  // Общая обёртка: сетевая ошибка → TelegramApiError(network), !ok → парсер,
  // 204 → undefined. request/requestForm отличаются только способом fetch-а.
  private async rawRequest<T>(fetcher: () => Promise<Response>): Promise<T> {
    let res: Response;
    try {
      res = await fetcher();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      throw new TelegramApiError(networkError(msg));
    }
    if (!res.ok) {
      throw new TelegramApiError(await parseTelegramErrorResponse(res));
    }
    if (res.status === 204) {
      return undefined as unknown as T;
    }
    return (await res.json()) as T;
  }

  private request<T>(url: string, init?: RequestInit): Promise<T> {
    return this.rawRequest<T>(() => authService.fetchWithAuth(url, init));
  }

  private requestForm<T>(url: string, fd: FormData): Promise<T> {
    return this.rawRequest<T>(() =>
      authService.fetchWithAuthForm(url, { method: 'POST', body: fd }),
    );
  }
}

const telegramService = new TelegramService();
export default telegramService;
